import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import MaterialCatalogAutocomplete from "@/components/MaterialCatalogAutocomplete";

export default function MaterialTab({ chantier, orders, onChanged, isAdmin }) {
  const [items, setItems] = useState([{ _key: "l_init", label: "", quantity: 1 }]);
  const [supplier, setSupplier] = useState("");
  const [saving, setSaving] = useState(false);

  const addLine = () => setItems((it) => [...it, { _key: `l_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, label: "", quantity: 1 }]);
  const updateLine = (i, k, v) => setItems((it) => it.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const rmLine = (i) => setItems((it) => it.filter((_, idx) => idx !== i));

  const save = async () => {
    const clean = items.filter((i) => i.label.trim());
    if (!clean.length) return toast.error("Ajoutez au moins un article");
    setSaving(true);
    try {
      await api.post("/material-orders", {
        chantier_id: chantier.id,
        supplier,
        items: clean.map((c) => ({ label: c.label, quantity: parseInt(c.quantity) || 1 })),
      });
      setItems([{ _key: "l_init", label: "", quantity: 1 }]);
      setSupplier("");
      onChanged();
      toast.success("Commande enregistrée");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-3">
        <div className="text-[11px] uppercase tracking-widest text-white/50">Nouvelle commande matériel</div>
        <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="Fournisseur (Rexel, Sonepar…)" className="bg-white/5 border-white/10 h-10" data-testid="material-supplier" />
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={it._key || i} className="grid gap-2" style={{ gridTemplateColumns: "1fr 60px 32px" }} data-testid={`material-line-${i}`}>
              <MaterialCatalogAutocomplete
                value={it.label}
                onChange={(v) => updateLine(i, "label", v)}
                onSelect={(item) => updateLine(i, "label", item.name)}
                testid={`material-line-${i}-label`}
              />
              <Input value={it.quantity} onChange={(e) => updateLine(i, "quantity", e.target.value)} type="number" min="1" placeholder="Qté" className="bg-white/5 border-white/10 h-10 text-center" />
              <button onClick={() => rmLine(i)} className="text-white/40 hover:text-red-400"><Trash size={14} /></button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={addLine} className="flex-1 border border-white/10">+ Ligne</Button>
          <Button onClick={save} disabled={saving} className="flex-1 bg-[#005CFF] hover:bg-[#0047CC]" data-testid="material-save">
            {saving ? "…" : "Enregistrer"}
          </Button>
        </div>
      </div>

      <div className="text-[11px] uppercase tracking-widest text-white/50">Historique ({orders.length})</div>
      {orders.length === 0 ? (
        <div className="rounded-2xl bg-white/[0.03] border border-dashed border-white/10 p-6 text-center text-white/40 text-sm">Aucune commande</div>
      ) : (
        <div className="space-y-2">
          {orders.map((o) => (
            <div key={o.id} className="rounded-2xl bg-white/[0.03] border border-white/10 p-3" data-testid={`order-${o.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">{o.supplier || "Fournisseur ?"}</div>
                  <div className="text-xs text-white/50">{new Date(o.created_at).toLocaleString("fr-FR")} · {o.created_by_name}</div>
                </div>
              </div>
              <div className="mt-2 space-y-1">
                {o.items.map((it, idx) => (
                  <div key={`${o.id}-item-${idx}`} className="text-sm text-white/70">
                    {it.quantity} × {it.label}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
